/* eslint-disable @blitz/lines-around-comment */
import { atom } from 'nanostores';

/**
 * Represents a selected element from the Canvas view.
 * Stores enough information to identify the element and display
 * a reference in the chat input.
 */
export interface CanvasElementSelection {
  /** CSS selector path to the element */
  selector: string;
  /** Tag name (e.g. "h1", "div", "button") */
  tagName: string;
  /** Element's id attribute, if any */
  id: string;
  /** Element's class name, if any */
  className: string;
  /** Text content of the element (first 80 chars) */
  textContent: string;
  /** Bounding rect relative to the iframe viewport */
  rect: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  /** Key computed styles of the selected element */
  computedStyles: Record<string, string>;
  /** Best-effort workspace path (e.g. src/App.tsx) from source scan */
  sourceFilePath?: string;
  /** 1-based line in sourceFilePath when resolved */
  sourceLine?: number;
  /** 0-based column in sourceFilePath when resolved */
  sourceColumn?: number;
}

/**
 * Global store for the currently selected canvas element.
 * Set to `null` when no element is selected.
 */
export const canvasElementSelectionStore = atom<CanvasElementSelection | null>(null);

/**
 * Select an element from the canvas.
 */
export function selectCanvasElement(element: CanvasElementSelection) {
  canvasElementSelectionStore.set(element);
}

/**
 * Clear the current canvas element selection.
 */
export function clearCanvasElementSelection() {
  canvasElementSelectionStore.set(null);
}

/** Update iframe viewport rect for the current selection (e.g. after scroll inside preview). */
export function updateCanvasSelectionRect(rect: CanvasElementSelection['rect']) {
  const cur = canvasElementSelectionStore.get();

  if (!cur) {
    return;
  }

  canvasElementSelectionStore.set({ ...cur, rect });
}

/**
 * Build a human-readable label for the selected element,
 * e.g. "h1#title" or "div.hero-section" or "button"
 */
export function getElementLabel(el: CanvasElementSelection): string {
  let label = el.tagName;

  if (el.id) {
    label += `#${el.id}`;
  } else if (el.className) {
    // Take first class only
    const firstClass = el.className.split(' ')[0];

    if (firstClass) {
      label += `.${firstClass}`;
    }
  }

  return label;
}

/**
 * Build a context string describing the selected element
 * to be prepended/appended to the user's chat prompt.
 */
export function getElementContextForPrompt(el: CanvasElementSelection): string {
  const label = getElementLabel(el);
  const textPreview = el.textContent ? ` with text "${el.textContent.slice(0, 60)}"` : '';

  const styleEntries = Object.entries(el.computedStyles)
    .filter(([, v]) => v && v !== 'none' && v !== 'normal' && v !== 'auto')
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');

  const sourceHint =
    el.sourceFilePath != null && el.sourceFilePath.length > 0
      ? ` | Source: ${el.sourceFilePath}${el.sourceLine != null ? `:${el.sourceLine}` : ''}${el.sourceColumn != null ? `:${el.sourceColumn}` : ''}`
      : '';

  return `Selected element: <${label}>${textPreview} | Selector: ${el.selector}${styleEntries ? ` | Styles: ${styleEntries}` : ''}${sourceHint}]`;
}

/**
 * Holds a formatted annotation string ready to be inserted into the chat textarea.
 * Set by `saveCanvasAnnotation`, consumed by the chat input component.
 */
export const canvasPendingAnnotationStore = atom<string | null>(null);

/**
 * Format the full element context with the user's instruction and queue it
 * for insertion into the chat textarea. Clears the active selection afterward.
 */
export function saveCanvasAnnotation(element: CanvasElementSelection, instruction: string) {
  const base = getElementContextForPrompt(element);
  const annotated = `${base.slice(0, -1)} | Instruction: ${instruction}`;
  canvasPendingAnnotationStore.set(annotated);
  clearCanvasElementSelection();
}

/**
 * Read and clear the pending annotation. Returns `null` if nothing is pending.
 */
export function consumePendingAnnotation(): string | null {
  const val = canvasPendingAnnotationStore.get();
  canvasPendingAnnotationStore.set(null);
  return val;
}
